import { getDashboardPath } from "@/lib/authRoutes";
import { Link, NavLink } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { Star, Search, Building2, Menu, X } from "lucide-react";
import { useState, useEffect } from "react";

export const LandingPageHeader = ({ transparent = false }: { transparent?: boolean }) => {
  const { session, profile, loading } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const dashboardPath = getDashboardPath(profile?.role) ?? "/login";
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on resize
  useEffect(() => {
    if (mobileOpen && window.innerWidth >= 768) setMobileOpen(false);
  }, [mobileOpen]);

  const navLinks = [
    { to: "/candidato/vagas", label: " buscar Vagas", icon: <Search className="w-4 h-4" /> },
    { to: "/para-empresas", label: "Para Empresas", icon: <Building2 className="w-4 h-4" /> },
  ];

  return (
    <>
      <header
        className={cn(
          "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
          scrolled || !transparent
            ? "bg-white/95 backdrop-blur-xl border-b border-gray-100/80 shadow-sm"
            : "bg-transparent"
        )}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-[68px]">
            
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2.5 group">
              <div className="relative w-9 h-9 flex-shrink-0">
                <div className="w-9 h-9 bg-gps-medium-blue rounded-xl flex items-center justify-center shadow-md group-hover:shadow-gps-medium-blue/40 transition-shadow">
                  <div className="w-4 h-4 bg-gps-yellow rotate-45 rounded-sm"></div>
                </div>
              </div>
              <span className={cn(
                "text-xl font-bold font-display tracking-tight transition-colors",
                scrolled || !transparent ? "text-gps-dark-blue" : "text-white"
              )}>
                GPS <span className="text-gps-medium-blue">Emprega</span>
              </span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-1">
              {navLinks.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  className={cn(
                    "flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200",
                    scrolled || !transparent
                      ? "text-gray-600 hover:text-gps-dark-blue hover:bg-gray-50"
                      : "text-white/80 hover:text-white hover:bg-white/10"
                  )}
                >
                  {link.icon}
                  {link.label}
                </NavLink>
              ))}
              
              {/* Premium Link */}
              <Link
                to="/vendas"
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold text-gps-yellow hover:bg-gps-yellow/10 transition-all duration-200"
              >
                <Star className="w-3.5 h-3.5 fill-current" />
                Premium
              </Link>
            </nav>

            {/* CTA Buttons */}
            <div className="hidden md:flex items-center gap-3">
              {session ? (
                <Button asChild size="sm" disabled={loading} className="bg-gps-medium-blue hover:bg-gps-dark-blue text-white rounded-xl px-5 shadow-md hover:shadow-lg transition-all">
                  <Link to={dashboardPath}>Meu Painel</Link>
                </Button>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    asChild
                    className={cn(
                      "rounded-xl px-4 font-medium",
                      scrolled || !transparent ? "text-gray-700 hover:bg-gray-50" : "text-white hover:bg-white/10"
                    )}
                  >
                    <Link to="/login">Entrar</Link>
                  </Button>
                  <Button
                    size="sm"
                    asChild
                    className="bg-gps-medium-blue hover:bg-gps-dark-blue text-white rounded-xl px-5 shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all"
                  >
                    <Link to="/cadastro">Criar Conta</Link>
                  </Button>
                </>
              )}
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label={mobileOpen ? "Fechar menu" : "Abrir menu"}
              aria-expanded={mobileOpen}
              className={cn(
                "md:hidden p-2.5 rounded-xl transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center",
                scrolled || !transparent ? "text-gray-700 hover:bg-gray-100" : "text-white hover:bg-white/10"
              )}
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 shadow-xl">
            <div className="container mx-auto px-4 py-4 space-y-1 max-h-[calc(100vh-64px)] overflow-y-auto">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors min-h-[48px]"
                >
                  {link.icon} {link.label}
                </Link>
              ))}
              <Link
                to="/vendas"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-bold text-gps-yellow hover:bg-gps-yellow/10 transition-colors min-h-[48px]"
              >
                <Star className="w-3.5 h-3.5 fill-current" /> Premium
              </Link>
              <div className="pt-3 border-t border-gray-100 flex flex-col gap-2">
                {session ? (
                  <Button asChild disabled={loading} className="w-full bg-gps-medium-blue hover:bg-gps-dark-blue text-white rounded-xl shadow-md min-h-[48px]">
                    <Link to={dashboardPath} onClick={() => setMobileOpen(false)}>Meu Painel</Link>
                  </Button>
                ) : (
                  <>
                    <Button asChild variant="outline" className="w-full rounded-xl min-h-[48px]">
                      <Link to="/login" onClick={() => setMobileOpen(false)}>Entrar</Link>
                    </Button>
                    <Button asChild className="w-full rounded-xl bg-gps-medium-blue min-h-[48px]">
                      <Link to="/cadastro" onClick={() => setMobileOpen(false)}>Criar Conta</Link>
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Spacer to offset fixed header */}
      <div className={cn("h-16 md:h-[68px]", transparent && "hidden")} />
    </>
  );
};